/**
 * StorylineDetailPage — IVT Hub Storyline detail (`/ivt/storylines/:storylineId`),
 * storyline-design.md §3 / Figma 3246:43741.
 *
 * Left = the SAME collapsible AssetNavTree as the Assets pages, in the "storyline"
 * (Storyline Menu) view with the current storyline highlighted. Right = title row
 * (name + Delete Storyline) + the Storyline Description card (inline edit).
 *
 * Right side: title row (name + Chat with KB / Edit / Delete) + the Storyline
 * Description card + the owner-generic Knowledge Base panel (same as Asset/UC detail,
 * keyed by this storyline). Chat with KB binds the chat panel to this storyline's scope.
 *
 * The nav tree needs the full asset list + storyline skeleton (same as the Assets
 * pages): useAssetsData for the list, useStorylineTreeProps for the shared Storyline
 * Menu wiring (skeleton + Create modal). The right side reads one shared GetStoryline
 * (useStoryline).
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Modal, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useAssetsData } from '../AssetsPage/hooks/useAssetsData';
import { useStoryline } from '../AssetsPage/hooks/useStoryline';
import { useStorylineTreeProps } from '../AssetsPage/hooks/useStorylineTreeProps';
import AssetNavTree from '../AssetsPage/components/AssetNavTree';
import CollapsibleTreePanel from '../AssetsPage/components/CollapsibleTreePanel';
import StorylineModal from '../AssetsPage/components/StorylineModal';
import { saveAssetsView } from '../AssetsPage/components/AssetsViewSwitch';
import { storylineClient } from '../../client/storylineClient';
import { useKb } from '../AssetsPage/hooks/useKb';
import { KbFilesPanel } from '../AssetsPage/components/AssetKbFiles';
import { useChatPanel } from '../../../context/ChatPanelContext';
import StorylineDescriptionCard from './components/StorylineDescriptionCard';
import './StorylineDetailPage.css';
import { denialHint } from '../../utils/authorization';

export default function StorylineDetailPage() {
  const { storylineId } = useParams<{ storylineId: string }>();
  const navigate = useNavigate();
  const { assets, industries, reload: reloadAssets } = useAssetsData();
  const { storyline, loading, error, reload } = useStoryline(storylineId ?? null);
  // Storyline KB — the SAME owner-generic hook + panel as the Asset/UC detail KB. Keyed
  // by the storyline's UUID (owner-generic; backend isolates chunks by a storyline_id
  // metadata key). Null id (route not resolved) → useKb no-ops to an empty panel.
  const kb = useKb('STORYLINE', storylineId ?? null);
  // Chat with KB — bind the chat panel to THIS storyline (sticky, exclusive with any
  // asset/UC scope), then expand it. Every subsequent user message carries the storyline
  // in context.items so the agent can call retrieve_storyline_kb.
  const { setStorylineScope, chatCollapsed, toggleChat } = useChatPanel();
  const chatWithKb = () => {
    if (!storyline) return;
    setStorylineScope({ storylineId: storyline.id ?? storylineId ?? '', storylineName: storyline.name ?? '' });
    if (chatCollapsed) toggleChat();
  };
  // Edit needs the FULL asset list (the modal's Choose-Assets options); `assets` is
  // null until ListAssets resolves (and stays null if it failed). Gate Edit on it —
  // editing with an incomplete list would silently drop unloaded linked assets (SET).
  const assetsReady = assets != null;

  const all = useMemo(() => assets ?? [], [assets]);
  // All Storyline-Menu tree wiring in one place. The shared hook bumps its own reload
  // key after a create, which matters HERE especially: creating from this page
  // navigates to the new storyline's detail — the same /ivt/storylines/:id route, so
  // React Router does NOT remount this page and the tree's ListStorylines fetch would
  // otherwise stay stale (missing the new storyline).
  const storylineTree = useStorylineTreeProps({
    assets: all,
    industries,
    selectedStorylineId: storylineId ?? null,
    reloadAssets, // after a create, re-fetch assets so picked ones hang under the new storyline
  });

  // Industry universe for the Edit modal's (read-only) Industry field.
  const industryOrder = useMemo(
    () => (industries ?? []).map((i) => i.industry).filter((n): n is string => !!n),
    [industries],
  );
  // Edit Storyline modal (StorylineModal mode='edit') — opened by the title-row Edit
  // button; edits name + linked-asset set in one atomic UpdateStoryline (SET semantics).
  const [editing, setEditing] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    if (!storylineId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await storylineClient.remove(storylineId);
      navigate('/ivt/assets'); // back to Overview after delete
    } catch (e) {
      setDeleteError(denialHint(e) ?? '删除 Storyline 失败，请重试。');
      setDeleting(false);
    }
  }

  return (
    <div className="ivt-storyline-page">
      <div className="ivt-storyline-shell">
        <CollapsibleTreePanel>
          <AssetNavTree
            assets={all}
            selectedAssetId={null}
            onSectionClick={(view) => {
              saveAssetsView(view);
              navigate('/ivt/assets');
            }}
            onUseCaseClick={(id) => navigate(`/ivt/use-cases/${id}`)}
            // Storyline Menu mode: full wiring from the shared hook — including
            // selectedStorylineId so THIS storyline auto-expands + highlights.
            {...storylineTree.treeProps}
          />
        </CollapsibleTreePanel>

        <div className="ivt-storyline-right">
          {loading && (
            <div className="ivt-storyline-right__loading">
              <Spinner size="large" />
              <span>加载 Storyline…</span>
            </div>
          )}

          {!loading && error && (
            <Alert type="error" header="无法加载 Storyline" action={<Button onClick={reload}>重试</Button>}>
              {error}
            </Alert>
          )}

          {!loading && !error && !storyline && (
            <Alert type="warning" header="Storyline 不存在">
              该 Storyline 可能已被删除。
              <Box margin={{ top: 's' }}>
                <Button onClick={() => navigate('/ivt/assets')}>返回 Assets</Button>
              </Box>
            </Alert>
          )}

          {!loading && !error && storyline && (
            <SpaceBetween size="l">
              <div className="ivt-storyline-titlerow">
                <span className="ivt-storyline-title" title={storyline.name}>
                  {storyline.name}
                </span>
                <SpaceBetween direction="horizontal" size="xs">
                  {/* Chat with KB — binds the chat panel to THIS storyline's scope so the
                      agent can call retrieve_storyline_kb. */}
                  <Button iconName="gen-ai" onClick={chatWithKb}>
                    Chat with KB
                  </Button>
                  {/* Edit name + linked assets (StorylineModal edit mode). Disabled until
                      ListAssets has loaded: the modal's Choose-Assets options come from
                      `all`, and SET semantics mean editing with an incomplete asset list
                      could silently drop still-unloaded linked assets. */}
                  <Button
                    variant="normal"
                    iconName="edit"
                    disabled={!assetsReady}
                    disabledReason={!assetsReady ? '正在加载 Asset 列表…' : undefined}
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                  <Button variant="normal" onClick={() => setConfirmDelete(true)}>
                    Delete Storyline
                  </Button>
                </SpaceBetween>
              </div>

              <StorylineDescriptionCard
                storylineId={storyline.id ?? ''}
                description={storyline.description ?? ''}
                updatedAt={storyline.updatedAt}
                onSaved={reload}
              />

              {/* Knowledge Base — same owner-generic panel as the Asset/UC detail KB,
                  keyed by this storyline. Upload / list / download / delete / folders /
                  URL entries / Overview all work identically. */}
              <div className="ivt-storyline-detail__kb">
                <KbFilesPanel kb={kb} />
              </div>
            </SpaceBetween>
          )}
        </div>
      </div>

      {/* Delete confirmation — destructive, cascades storyline_asset_link (backend). */}
      <Modal
        visible={confirmDelete}
        onDismiss={() => !deleting && setConfirmDelete(false)}
        header="删除 Storyline"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled={deleting} onClick={() => setConfirmDelete(false)}>
                取消
              </Button>
              <Button variant="primary" loading={deleting} onClick={() => void handleDelete()}>
                删除
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {deleteError && (
          <Box margin={{ bottom: 's' }}>
            <Alert type="error">{deleteError}</Alert>
          </Box>
        )}
        确定删除「{storyline?.name}」吗？该 Storyline 与 Asset 的关联会一并解除（Asset 本身不受影响）。此操作不可恢复。
      </Modal>

      {/* Create Storyline modal (opened by the Storyline Menu tree's `+`). */}
      {storylineTree.modalNode}

      {/* Edit Storyline modal — name + linked-asset set (industry read-only). Prefilled
          from GetStoryline (name + linkedAssets). On save: reload the detail (name/
          assets), the tree skeleton (node label/count), and ListAssets (assets whose
          storylineLinks changed re-hang in the tree). */}
      {editing && storyline && assetsReady && (
        <StorylineModal
          mode="edit"
          assets={all}
          industries={industryOrder}
          initial={{
            storylineId: storyline.id ?? storylineId,
            name: storyline.name,
            industry: storyline.industry,
            assetIds: (storyline.linkedAssets ?? [])
              .map((a) => a.assetId)
              .filter((id): id is string => !!id),
          }}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload(); // detail: name + linkedAssets
            storylineTree.reloadStorylines(); // tree node label/count
            reloadAssets(); // assets whose storylineLinks changed
          }}
        />
      )}
    </div>
  );
}
